/**
 * Presentacion de cantidades.
 *
 * El servidor manda las cantidades como cadena con tres decimales fijos
 * («1.000»), porque un decimal que pasa por `number` deja de ser exacto
 * (ADR-026). Eso es correcto para transportar el dato y pesimo para leerlo: en
 * espanol el punto tambien separa los millares, asi que «1.000 Caja» se lee
 * como mil cajas cuando en realidad hay una. En un sistema de inventario esa
 * ambiguedad no es cosmetica, es un error de conteo.
 *
 * Aqui se corrige en el borde de la pantalla y solo ahi: se recortan los ceros
 * finales y se agrupan los millares con coma, que es la convencion de es-MX.
 *
 * El formateo trabaja sobre la cadena, nunca sobre `number`. No es
 * desconfianza gratuita hacia `Intl`: convertir a `number` para volver a texto
 * reintroduce justo el paso que ADR-026 prohibe, y aqui no hace falta ninguna
 * aritmetica para colocar comas y quitar ceros.
 */

/** Cantidad tal como viaja: enteros, con signo opcional y hasta tres decimales. */
const QUANTITY_PATTERN = /^-?\d+(\.\d+)?$/;

/**
 * Convierte «1.000» en «1», «1500.500» en «1,500.5» y «0.250» en «0.25».
 *
 * Un valor que no tenga forma de cantidad se devuelve intacto: la pantalla
 * prefiere mostrar el dato crudo antes que inventarse uno.
 */
export function formatQuantity(value: string): string {
  const trimmed = value.trim();

  if (!QUANTITY_PATTERN.test(trimmed)) {
    return value;
  }

  const isNegative = trimmed.startsWith('-');
  const [whole = '0', decimals = ''] = (isNegative ? trimmed.slice(1) : trimmed).split('.');
  const fraction = decimals.replace(/0+$/, '');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${isNegative ? '-' : ''}${grouped}${fraction === '' ? '' : `.${fraction}`}`;
}

/** La cantidad con su unidad, para los textos donde no cabe un componente. */
export function formatQuantityWithUnit(value: string, unit: string | null): string {
  const quantity = formatQuantity(value);
  return unit === null || unit === '' ? quantity : `${quantity} ${unit}`;
}
